import { Box } from '@mui/material';
import { usePrevious } from 'common';
import { LruObjectCache } from 're-reselect';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  List,
  ListRowProps,
  WindowScroller,
} from 'react-virtualized';
import { debounce } from 'throttle-debounce';
import { BreakpointProperties } from '../../../../../hooks/useBreakpoint';
import { ScreenOrientationType } from '../../../../../hooks/useScreenOrientation';
import {
  CfDataGridCardViewDefinition,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridRowType,
  CfRecomputeRowHeightsRefCallback,
  CfRowData,
  NavigableElement,
} from '../../../CfDataGridProps';
import {
  detectScrollBottom,
  getColumnCount,
  getIsLoading,
  getItemIdsFromDataGridProps,
  getLastPage,
  getPagingMode,
  getTotalPageCount,
} from '../../../utility/data-grid-props';
import { getEstimatedRowHeight } from '../../../utility/estimate-row-heights';
import { overrideListScrollTo } from '../../../utility/scroll-to';
import {
  detectCardRowsChanged,
  getVirtualizedCardRows,
  recomputeCardRowHeights,
  recomputeCardRowHeightsByIndex,
  recomputeCardRowHeightsFromIndex,
} from './common';

/* 
Caches
*/
const scrollToCache = new LruObjectCache({ cacheSize: 3 });
function getScrollTo(id: string): { scrollTop: number; itemIndex: number } | undefined {
  return scrollToCache.get(id);
}
function setScrollTo(id: string, itemIndex?: number | undefined): void {
  if (itemIndex === undefined) {
    scrollToCache.remove(id);
  } else {
    const scrollTop = document.documentElement.scrollTop;
    scrollToCache.set(id, { scrollTop, itemIndex });
  }
}

const cellMeasurerCaches = new LruObjectCache({ cacheSize: 3 });
function getCache(
  id: string,
  getEstimatedAverageRowHeight: ((type: CfDataGridRowType) => number) | undefined
): CellMeasurerCache {
  let cache = cellMeasurerCaches.get(id);
  if (!cache) {
    cache = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: getEstimatedAverageRowHeight?.('card-row') ?? 470,
    });
    cellMeasurerCaches.set(id, cache);
  }
  return cache;
}

/* 
Virtualized Card Body
*/
export function CfDataGridCardViewVirtualizedBody<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  cardViewDefinition: CfDataGridCardViewDefinition<TItem>;
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void;
  breakpointProperties: BreakpointProperties;
  orientation: ScreenOrientationType;
  onNextPageRequested: (pageIndex: number) => void;
  focusClosestEnabledElement: (rowIndex: number, elementIndex?: number) => void;
  recomputeRowHeightsRef: CfRecomputeRowHeightsRefCallback;
}): JSX.Element {
  /* 
  List 
  */
  const listRef = useRef<List | null>(null);

  /* 
  Constants 
  */
  const scrollTo = getScrollTo(props.dataGridProps.id);
  const cache = getCache(
    props.dataGridProps.id,
    props.cardViewDefinition.virtualizedProps?.getEstimatedAverageRowHeight
  );

  const gridId = props.dataGridProps.id;
  const virtualizedProps = props.cardViewDefinition.virtualizedProps;
  const contained = virtualizedProps?.contained ?? false;

  const isLoading = getIsLoading(props.dataGridProps);
  const lastPage = getLastPage(props.dataGridProps);
  const pagingMode = getPagingMode(props.dataGridProps);
  const totalPageCount = getTotalPageCount(props.dataGridProps);
  const columnCount = getColumnCount(props.cardViewDefinition, props.orientation);
  const dataGridIds = getItemIdsFromDataGridProps(props.dataGridProps);

  /* 
  Functions 
  */
  const setListRef = (list: List | null) => {
    listRef.current = list;
    if (list) overrideListScrollTo(list);

    virtualizedProps?.recomputeRowHeightsRef?.(list ? () => recomputeCardRowHeights(listRef, cache) : null);
    virtualizedProps?.scrollIndexRef?.(list ? (i: number) => setScrollTo(gridId, i) : null);
    props.recomputeRowHeightsRef(() => {
      cache.clearAll();
    });
  };

  const recomputeRowHeights = () => recomputeCardRowHeights(listRef, cache);
  const recomputeRowHeightsFromIndex = (i: number) =>
    recomputeCardRowHeightsFromIndex(listRef, cache, columnCount, itemIds.length, i);
  const recomputeRowHeightsByIndex = (i: number) => recomputeCardRowHeightsByIndex(listRef, cache, i);

  const _debouncedRecomputeRowHeights = debounce(100, true, recomputeRowHeights);
  const _debouncedOnScrollBottom = debounce(150, true, detectScrollBottom);

  const debouncedRecomputeRowHeights = useCallback(_debouncedRecomputeRowHeights, [listRef]);
  const debouncedOnScrollBottom = useCallback(_debouncedOnScrollBottom, [listRef]);

  /* 
  Hooks
  */
  const { itemIds, rows } = useMemo(
    () =>
      getVirtualizedCardRows(
        props.dataGridProps,
        props.cardViewDefinition,
        props.setNavigableElement,
        props.orientation,
        recomputeRowHeightsByIndex,
        debouncedRecomputeRowHeights
      ),
    [props.dataGridProps.id, isLoading, props.orientation, props.breakpointProperties.deviceType, dataGridIds]
  );

  const prevIsLoading = usePrevious(isLoading);
  const prevDeviceType = usePrevious(props.breakpointProperties.deviceType);
  const prevItemIds = usePrevious(itemIds);
  const prevOrientation = usePrevious(props.orientation);

  /* 
  Effects
  */
  useEffect(() => {
    if (!listRef.current || !lastPage || scrollTo === undefined) return;

    // Handle scroll-to
    if (prevIsLoading === undefined && isLoading === false) {
      const rowIndex = Math.floor(scrollTo.itemIndex / columnCount);
      const columnIndex = scrollTo.itemIndex % columnCount;

      listRef.current?.scrollToPosition?.(scrollTo.scrollTop);
      props.focusClosestEnabledElement(rowIndex, columnIndex);
      scrollToCache.remove(gridId);
    } else {
      scrollToCache.remove(gridId);
    }
  }, [isLoading, scrollTo]);

  useEffect(() => {
    if (virtualizedProps?.getRowHeight || !listRef.current || !lastPage) return;

    // Handle device type
    if (
      prevDeviceType !== undefined &&
      (props.breakpointProperties.deviceType !== prevDeviceType || props.orientation !== prevOrientation)
    ) {
      //console.log('device type change');
      recomputeRowHeights();
      setTimeout(recomputeRowHeights, 20);
    }
  }, [props.breakpointProperties.deviceType, props.orientation]);

  useEffect(() => {
    if (virtualizedProps?.getRowHeight || !listRef.current || !lastPage) return;

    // Handle load
    if (lastPage.pageIndex === 0 && prevIsLoading != isLoading) {
      if (prevIsLoading === undefined && isLoading === false) return; // no-op, returning to page with grid data
      recomputeRowHeights();
    } else if (!isLoading) {
      const { index, differenceFound } = detectCardRowsChanged(itemIds, prevItemIds);

      if (differenceFound) recomputeRowHeightsFromIndex(index);
    }
  }, [isLoading, itemIds]);

  useEffect(() => {
    return () => {
      _debouncedRecomputeRowHeights.cancel();
      _debouncedOnScrollBottom.cancel();
    };
  }, []);

  /* 
  JSX 
  */
  const listPropsBase = {
    ref: setListRef,
    deferredMeasurementCache: cache,
    rowCount: rows.length,
    rowHeight: ({ index }: Index) => virtualizedProps?.getRowHeight?.(rows[index].type) ?? cache.rowHeight({ index }),
    rowRenderer: getRowRender(rows, cache),
    overscanRowCount: virtualizedProps?.overscanRowCount ?? 2,
    estimatedRowSize: getEstimatedRowHeight(
      rows,
      cache,
      virtualizedProps?.getEstimatedAverageRowHeight,
      prevIsLoading ?? false
    ),
    style: { marginTop: '-16px' },
    tabIndex: -1,
  };

  if (contained) {
    return <AutoSizer>{({ width, height }) => <List {...listPropsBase} width={width} height={height} />}</AutoSizer>;
  }
  return (
    <WindowScroller>
      {({ height, isScrolling, onChildScroll, scrollTop }) => (
        <AutoSizer disableHeight>
          {({ width }) => (
            <List
              {...listPropsBase}
              width={width}
              height={height}
              autoHeight
              isScrolling={isScrolling}
              onScroll={(params) => {
                debouncedOnScrollBottom(params, lastPage, pagingMode, totalPageCount, props.onNextPageRequested);
                onChildScroll(params);
              }}
              scrollTop={scrollTop}
              style={{ outline: 'none' }}
            />
          )}
        </AutoSizer>
      )}
    </WindowScroller>
  );
}

const getRowRender = (rows: CfRowData[], cache: CellMeasurerCache) =>
  function rowRender(props: ListRowProps): JSX.Element | null {
    return (
      <CellMeasurer cache={cache} key={props.key} parent={props.parent} rowIndex={props.index}>
        {({ registerChild }) => (
          <Box ref={registerChild} style={props.style}>
            {rows[props.index].content}
          </Box>
        )}
      </CellMeasurer>
    );
  };
